/**
 * Path roots whose ENTIRE subtree is a capability URL — `/promotion` and
 * `/promotion/<token>` alike. Exported as data, not folded into the predicate
 * below, because a second consumer needs the same list in a place a TypeScript
 * import cannot reach: the Sentry Loader's `beforeSend` runs inside an inline
 * `<script>`, and it serializes THIS array (see
 * `lib/error-reporting/sentry-on-load-snippet.ts`). One list, two readers —
 * adding a capability URL here covers analytics and error reporting together.
 */
export const ANALYTICS_EXCLUDED_PATH_ROOTS: readonly string[] = [
  '/promotion',
  // Same shape as `/promotion/<token>`: the path IS the invite, so anyone who
  // can read it can take the seat it was addressed to.
  '/invite',
  '/share',
  // Survey admin: the editor and responses pages show respondents' emails and
  // answers, which must not reach session replay or error reports.
  '/admin/surveys',
  // `/demo/<token>`: the outreach demo sent to one prospect; the path is the link.
  '/demo',
  // `/anonymous/result/<shortId>`: reads the whole trial transcript without
  // signing in, and a signed-in visitor can claim it within 24 hours.
  '/anonymous/result',
]

/**
 * Pages whose secret rides ONLY in the fragment (`#…`). Excluded from
 * analytics like the exact paths below: the page clears the fragment on load,
 * but `page_location`, Pixel's `dl` and error reports read `location.href`,
 * fragment included.
 *
 * Unlike them they answer `Referrer-Policy: strict-origin`, not
 * `no-referrer` (`next.config.ts`): a browser never puts the fragment in a
 * Referer, and these pages call an API whose key is restricted by HTTP
 * referrer, so they must still send their origin.
 */
export const ANALYTICS_EXCLUDED_FRAGMENT_SECRET_PATHS: readonly string[] = [
  // `#code=…&state=…`: the mobile app's one-time code, redeemable for its
  // session. The Google Picker's API key is referrer-restricted.
  '/drive-pick',
]

/**
 * Pages whose request ALWAYS carries a secret in the query, so the exact
 * pathname is enough. `page_location` is `window.location.href`, so without
 * this the secret itself is handed to GA4 and Clarity, and anyone reading
 * those reports can redeem it.
 */
export const ANALYTICS_EXCLUDED_EXACT_PATHS: readonly string[] = [
  // `?token=`: redeemable for a session on the public endpoint.
  '/magic-login',
  // `?token=`: reads and changes one recipient's mail settings.
  '/email/preferences',
  // `?token=`: confirms one address.
  '/email/verify',
  // `?request_id=`: the pending grant the consent click approves.
  '/oauth/consent',
  // `?state=`: the nonce the desktop app waits on; the page hands a sign-in
  // code back to whoever holds it.
  '/desktop-login',
  ...ANALYTICS_EXCLUDED_FRAGMENT_SECRET_PATHS,
]

/**
 * Public pages that become a capability URL only when one query parameter is
 * present. `/blog/<slug>` is an article anyone may read and GA should see;
 * `/blog/<slug>?preview=<token>` unlocks a post that is not published yet.
 */
export const ANALYTICS_EXCLUDED_QUERY_PARAMS: readonly { root: string; param: string }[] = [
  { root: '/blog', param: 'preview' },
]

const underRoot = (pathname: string, root: string): boolean =>
  pathname === root || pathname.startsWith(`${root}/`)

/**
 * Capability URLs carry a bearer-like secret in the path or the query. They
 * must never be observed by third-party analytics or session replay.
 *
 * `search` is required, not optional: a caller that only passes the pathname
 * would quietly miss every query-borne secret. Pass `null` only where there
 * is genuinely no query to look at.
 */
export function isAnalyticsExcludedPath(
  pathname: string | null,
  search: string | URLSearchParams | null
): boolean {
  if (!pathname) return false

  if (ANALYTICS_EXCLUDED_EXACT_PATHS.includes(pathname)) return true
  if (ANALYTICS_EXCLUDED_PATH_ROOTS.some((root) => underRoot(pathname, root))) return true

  if (search === null) return false
  const query = typeof search === 'string' ? new URLSearchParams(search) : search
  return ANALYTICS_EXCLUDED_QUERY_PARAMS.some(
    ({ root, param }) => underRoot(pathname, root) && query.has(param)
  )
}

/**
 * Whether the page being viewed RIGHT NOW is one of those URLs.
 *
 * Read from `location` at the moment of dispatch rather than from a captured
 * `usePathname()`: an event reported after an `await` runs with the pathname of
 * whatever page the visitor is on when the promise settles, not the one that
 * started it. Someone who submits a form and navigates into a capability URL
 * while the request is in flight would otherwise be reported from there, which
 * is the exact leak this module exists to prevent.
 */
export function isOnAnalyticsExcludedPath(): boolean {
  return (
    typeof window !== 'undefined' &&
    isAnalyticsExcludedPath(window.location.pathname, window.location.search)
  )
}

/**
 * Path roots measured by GA4 only: no Meta Pixel and no Clarity session
 * replay. A survey page (`/s/<slug>`) is not a secret, so GA may see it and
 * the survey funnel is measured there; but the respondent types free-text
 * research answers, which session replay would record, and Pixel has no
 * business optimising ads on them. The survey is reached from an emailed
 * link, never by in-app navigation, so the scripts are simply not rendered
 * on these paths and Pixel dispatch is refused here as a second guard.
 */
export const ANALYTICS_GA_ONLY_PATH_ROOTS: readonly string[] = ['/s']

export function isAnalyticsGaOnlyPath(pathname: string | null): boolean {
  if (!pathname) return false
  return ANALYTICS_GA_ONLY_PATH_ROOTS.some((root) => underRoot(pathname, root))
}

/** Whether the page being viewed right now is GA-only; read at dispatch time like the one above. */
export function isOnAnalyticsGaOnlyPath(): boolean {
  return typeof window !== 'undefined' && isAnalyticsGaOnlyPath(window.location.pathname)
}

/**
 * Path roots where Clarity session replay never loads: every page that needs a
 * sign-in and shows the account, its content or its payments. GA and Pixel
 * still measure them. Signed-in pages that are also capability URLs
 * (`/oauth/consent`, `/desktop-login`, `/invite`, `/promotion`) load nothing
 * at all and are not repeated here.
 */
export const CLARITY_EXCLUDED_PATH_ROOTS: readonly string[] = [
  // The signed-in member area.
  '/app',
  // The staff console.
  '/admin',
  // Deleting the account (behind `AuthGuard`; the Android app opens it too).
  '/account-deletion',
  // Where TapPay returns the buyer after 3-D Secure: the signed-in payment result.
  '/billing/3ds-return',
]

/** Which analytics platforms a page may carry. */
export interface AnalyticsPlatforms {
  ga: boolean
  pixel: boolean
  clarity: boolean
}

/**
 * The one decision about which third-party script may run on a URL. The
 * loader (`<AnalyticsScripts>`) and the navigation guard
 * (`script-boundary.ts`) both read it, so what a page loads and what a page
 * must not be entered with cannot drift apart.
 */
export function analyticsPlatformsAllowedAt(
  pathname: string | null,
  search: string | URLSearchParams | null
): AnalyticsPlatforms {
  if (isAnalyticsExcludedPath(pathname, search)) return { ga: false, pixel: false, clarity: false }
  if (isAnalyticsGaOnlyPath(pathname)) return { ga: true, pixel: false, clarity: false }
  const clarity =
    !pathname || !CLARITY_EXCLUDED_PATH_ROOTS.some((root) => underRoot(pathname, root))
  return { ga: true, pixel: true, clarity }
}
